import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import API from "../lib/api";
import { Button } from "../components/ui/button";
import { CreditCard, CheckCircle, WarningCircle, Receipt, SpinnerGap } from "@phosphor-icons/react";
import { fmtDate } from "../lib/dateUtil";

/**
 * Public invoice payment page — /pay/invoice/:token
 * No login required. Amount is determined server-side from the invoice.
 */
export default function PayInvoicePage() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");

  const [invoice, setInvoice] = useState(null);
  const [error, setError] = useState("");
  const [paying, setPaying] = useState(false);
  const [payState, setPayState] = useState(sessionId ? "checking" : "idle"); // idle | checking | paid | expired | failed
  const pollRef = useRef(0);

  const load = useCallback(async () => {
    try {
      const { data } = await API.get(`/pay/invoice/${token}`);
      setInvoice(data);
      if (data.status === "paid") setPayState("paid");
    } catch (e) {
      setError(e.response?.status === 404 ? "This payment link is invalid or has been removed." : "Could not load invoice. Please try again.");
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // Poll checkout status when returning from Stripe
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      pollRef.current += 1;
      try {
        const { data } = await API.get(`/pay/invoice/${token}/checkout-status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid" || data.invoice_status === "paid") {
          setPayState("paid");
          load();
          return;
        }
        if (data.session_status === "expired") { setPayState("expired"); return; }
      } catch {
        /* keep polling */
      }
      if (pollRef.current >= 8) { setPayState("failed"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, token, load]);

  const startPayment = async () => {
    setPaying(true);
    try {
      const { data } = await API.post(`/pay/invoice/${token}/checkout`, {
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      setError(e.response?.data?.detail || "Could not start payment — please try again.");
      setPaying(false);
    }
  };

  const fmt = (n) => `£${Number(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4" data-testid="pay-invoice-page">
      <div className="w-full max-w-md">
        <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl overflow-hidden border border-slate-200 dark:border-slate-800">
          <div className="bg-slate-900 dark:bg-slate-950 px-6 py-5 text-white">
            <div className="flex items-center gap-3">
              {invoice?.logo_url ? (
                <img src={invoice.logo_url} alt="" className="h-9 w-9 rounded-md object-contain bg-white p-0.5" />
              ) : (
                <Receipt size={26} className="text-emerald-400" />
              )}
              <div>
                <p className="text-sm font-semibold" data-testid="pay-company-name">{invoice?.company_name || "Invoice Payment"}</p>
                <p className="text-[11px] text-slate-400">Secure invoice payment</p>
              </div>
            </div>
          </div>

          <div className="p-6">
            {error ? (
              <div className="text-center py-8" data-testid="pay-error">
                <WarningCircle size={36} className="mx-auto text-red-500 mb-2" />
                <p className="text-sm text-slate-600 dark:text-slate-300">{error}</p>
              </div>
            ) : !invoice ? (
              <div className="text-center py-10">
                <SpinnerGap size={28} className="mx-auto animate-spin text-slate-400" />
              </div>
            ) : payState === "paid" ? (
              <div className="text-center py-6" data-testid="pay-success">
                <CheckCircle size={44} weight="fill" className="mx-auto text-emerald-500 mb-3" />
                <p className="text-lg font-semibold text-slate-900 dark:text-white">Payment received</p>
                <p className="text-sm text-slate-500 mt-1">
                  Invoice <span className="font-mono font-medium">{invoice.reference}</span> — {fmt(invoice.amount)}
                </p>
                <p className="text-xs text-slate-400 mt-3">A receipt has been recorded. Thank you!</p>
              </div>
            ) : payState === "checking" ? (
              <div className="text-center py-8" data-testid="pay-checking">
                <SpinnerGap size={32} className="mx-auto animate-spin text-emerald-500 mb-3" />
                <p className="text-sm font-medium text-slate-700 dark:text-slate-200">Confirming your payment…</p>
                <p className="text-xs text-slate-400 mt-1">This usually takes a couple of seconds.</p>
              </div>
            ) : (
              <>
                <div className="space-y-3" data-testid="pay-summary">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Invoice</span>
                    <span className="font-mono font-semibold text-slate-900 dark:text-white">{invoice.reference}</span>
                  </div>
                  {invoice.title && (
                    <div className="flex justify-between text-sm gap-4">
                      <span className="text-slate-500 shrink-0">For</span>
                      <span className="text-right text-slate-700 dark:text-slate-200">{invoice.title}</span>
                    </div>
                  )}
                  {invoice.due_date && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Due date</span>
                      <span className="text-slate-700 dark:text-slate-200">{fmtDate(invoice.due_date)}</span>
                    </div>
                  )}
                  {invoice.vat_enabled && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Includes VAT</span>
                      <span className="text-slate-700 dark:text-slate-200">{fmt(invoice.vat_amount)}</span>
                    </div>
                  )}
                  <div className="border-t border-slate-200 dark:border-slate-700 pt-3 flex justify-between items-baseline">
                    <span className="text-sm font-medium text-slate-600 dark:text-slate-300">Amount due</span>
                    <span className="text-3xl font-bold text-slate-900 dark:text-white" data-testid="pay-amount">{fmt(invoice.amount)}</span>
                  </div>
                </div>

                {(payState === "expired" || payState === "failed") && (
                  <p className="text-xs text-amber-600 mt-3 text-center" data-testid="pay-retry-note">
                    {payState === "expired" ? "Your previous payment session expired — please try again." : "We couldn't confirm the payment yet. If you completed payment, it will reflect shortly; otherwise try again."}
                  </p>
                )}

                <Button
                  className="w-full mt-5 h-12 text-base bg-emerald-600 hover:bg-emerald-700 text-white"
                  onClick={startPayment}
                  disabled={paying}
                  data-testid="pay-now-btn"
                >
                  <CreditCard size={18} className="mr-2" />
                  {paying ? "Redirecting…" : `Pay ${fmt(invoice.amount)} by Card`}
                </Button>
                <p className="text-[10px] text-slate-400 text-center mt-3">Payments are processed securely by Stripe. We never see your card details.</p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
